import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { MaterialDto, ObligationMaterialsResponse, StockMoveDto } from "@klantpunt/shared";
import { api, ApiError } from "../../lib/api";
import { hasModule, useMe } from "../../lib/auth";

export const materialsKey = (buildingId: string) => ["materials", buildingId] as const;
export const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");

/** Artikellijst van een gebouw (van de klant en van het gebouw zelf), met de voorraad in dat gebouw. */
export function useMaterials(buildingId: string, opts: { archived?: boolean; enabled?: boolean } = {}) {
  const { data: me } = useMe();
  return useQuery({
    queryKey: [...materialsKey(buildingId), opts.archived ? "archief" : "actief"],
    queryFn: () => api<MaterialDto[]>(`/buildings/${buildingId}/materials${opts.archived ? "?archief=1" : ""}`),
    enabled: !!buildingId && hasModule(me, "materials") && opts.enabled !== false,
  });
}

export function useStockMoves(buildingId: string, materialId: string) {
  return useQuery({
    queryKey: [...materialsKey(buildingId), "moves", materialId],
    queryFn: () => api<StockMoveDto[]>(`/buildings/${buildingId}/materials/${materialId}/moves`),
  });
}

export function useObligationMaterials(obligationId: string, enabled = true) {
  const { data: me } = useMe();
  return useQuery({
    queryKey: ["obligation-materials", obligationId],
    queryFn: () => api<ObligationMaterialsResponse>(`/asset-obligations/${obligationId}/materials`),
    enabled: enabled && hasModule(me, "materials"),
  });
}

/** Na een wijziging: artikellijst en voorraad van het gebouw opnieuw ophalen. */
export function useMaterialsRefresh(buildingId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: materialsKey(buildingId) });
}
